// © 2025 Camp Promethean, LLC. All Rights Reserved.
// Existing Selections Table Wrapper

import { Button } from '@/components/ui/button';
import { Plant } from '@/types/plant';
import { MoreHorizontal } from 'lucide-react';

interface ExistingSelectionsTableProps {
  projectId: string;
  onAddSelection: (plant?: Plant) => void;
}

export function ExistingSelectionsTable({ 
  projectId,
  onAddSelection 
}: ExistingSelectionsTableProps) {
  // This component wraps your existing table implementation
  // You would typically fetch the data here based on projectId
  
  // Sample plant data for the demo row
  const samplePlant: Plant = {
    id: "plant-1",
    plant_id: "Lemo-001",
    phenotype_id: "pheno-1",
    project_id: projectId,
    status: "active",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };
  
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium">Selected Phenotypes</h2>
        <p className="text-sm text-gray-500">Plants marked as selected for final evaluation</p>
      </div>
      
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">ID</th>
              <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">Stage</th>
              <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">Structure</th>
              <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">Vigor</th>
              <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">Aroma</th>
              <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">Overall</th>
              <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">Selection Notes</th>
              <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            <tr>
              <td className="py-3 px-4 text-sm">Lemo-001</td>
              <td className="py-3 px-4 text-sm text-primary">Germination</td>
              <td className="py-3 px-4">
                <div className="flex items-center">
                  <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full" style={{ width: '20%' }}></div>
                  </div>
                </div>
              </td>
              <td className="py-3 px-4">
                <div className="flex items-center">
                  <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 rounded-full" style={{ width: '20%' }}></div>
                  </div>
                </div>
              </td>
              <td className="py-3 px-4">
                <div className="flex items-center">
                  <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-purple-500 rounded-full" style={{ width: '20%' }}></div>
                  </div>
                </div>
              </td>
              <td className="py-3 px-4">
                <div className="flex items-center">
                  <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-red-500 rounded-full" style={{ width: '20%' }}></div>
                  </div>
                </div>
              </td>
              <td className="py-3 px-4 text-sm">-</td>
              <td className="py-3 px-4">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => onAddSelection(samplePlant)}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
} 